/**
 * SequenceController.tsx - Component for controlling experiment sequences
 * Last updated: 2025-08-13 09:03:06
 * Updated by: JulianPolly
 */

import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Button, Intent, NonIdealState, Size } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ScreenConfig, ScreenSequence, Sequences } from "./Sequences/Sequences";
import { useSession } from "../SessionContext";
import { produce } from "immer";
import { useImmer } from "use-immer";
import { useLanguage } from "../../context/LanguageContext";

type Result = any;

export type ScreenProps = {
    setCanContinue: (canContinue: boolean) => void,
    result: Result,
    setResult: (result: Result) => void | ((prevResult: Result) => Result),
}

const useWriteResult = (sequenceId: string, screenId: string) => {
    const { userData, writeUserData } = useSession();

    return (result: Result) => writeUserData(produce(userData, (draft: any) => {
        if (!draft[sequenceId]) {
            draft[sequenceId] = {};
        }
        draft[sequenceId][screenId] = result;
        return draft;
    }));
}

export const ScreenContainer: React.FC<{
    screen: ScreenConfig,
    sequence: ScreenSequence,
    sequenceId: string,
    screenId: string,
    screenIdInt: number
}> = ({ screen, sequence, sequenceId, screenId, screenIdInt }) => {
    const { t } = useLanguage();
    const [canContinue, setCanContinue] = useState<boolean>(screen?.canContinueDefault ?? true);
    const [result, setResult] = useImmer<object>(screen?.defaultResult ?? {});
    const navigate = useNavigate();
    const writeResult = useWriteResult(sequenceId || "", screenId || "");

    const continueNext = () => {
        writeResult(result).then(() => {
            const nextExperimentId = screenIdInt + 1;
            if (sequence.screens[nextExperimentId]) {
                // @ts-ignore
                navigate(`/app/sequences/${sequenceId}/${nextExperimentId}`, { replace: true });
            } else {
                navigate(`/app/sequences/${sequenceId}`);
            }
        }).catch(error => {
            console.error("Error writing result:", error);
            alert("Es gab einen Fehler beim Speichern des Ergebnisses. Bitte versuchen Sie es später erneut.");
            navigate(`/app/sequences/${sequenceId}`);
        });
    }

    return (
        <div>
            <div style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#f0f0f0",
                borderRadius: "10px",
                padding: "20px",
                boxSizing: "border-box",
                margin: "40px 0",
            }}>
                {React.createElement<ScreenProps>(screen.component, { setCanContinue, result, setResult })}
            </div>
            {/* BUTTON NUR ANZEIGEN, WENN canContinueDefault !== false */}
            {screen.canContinueDefault !== false && (
                <Button
                    onClick={continueNext}
                    disabled={!canContinue}
                    icon={undefined}
                    rightIcon={IconNames.ARROW_RIGHT}
                    intent={Intent.PRIMARY}
                >
                    {t('ui', 'next')}
                </Button>
            )}
        </div>
    );
};

export const SequenceController: React.FC = () => {
    const { t } = useLanguage();
    const { sequenceId, screenId } = useParams();

    const sequenceFactory = (sequenceId && Sequences[sequenceId]) ? Sequences[sequenceId] : undefined;
    const sequence = sequenceFactory ? sequenceFactory() : undefined;

    const screenIdInt = parseInt(screenId || "");
    const screen = (screenId && sequence?.screens[screenIdInt])
        ? sequence.screens[screenIdInt]
        : undefined;

    if (!sequence || !screen) {
        return (
            <div>
                <NonIdealState
                    icon={IconNames.ERROR}
                    title={t('ui', 'errors', 'sequenceNotFound', 'Experimentsequenz nicht gefunden')}
                    description={t('ui', 'errors', 'sequenceNotFoundDescription', 'Diese Sequenz wurde nicht gefunden. Bitte überprüfen Sie die URL.')}
                    action={
                        <Link to={'/app/'}>
                            <Button size={Size.LARGE} rightIcon={IconNames.HOME} text={t('ui', 'backToHome', 'Zur Hauptseite')} />
                        </Link>
                    }
                />
            </div>
        );
    } else {
        return (
            <ScreenContainer
                screen={screen}
                sequence={sequence}
                sequenceId={sequenceId!}
                screenId={screenId!}
                screenIdInt={screenIdInt}
                key={screenIdInt}
            />
        );
    }
};